/* eslint-disable react/no-unescaped-entities */
/**
 * OwnerDesk V1 · Phase 2 · PA Card (shared component).
 *
 * Renders the Property Advisor assigned to a case. Used in the
 * dashboard case card AND in the case detail page. When no PA is
 * assigned, an empty-slot with a matching CTA is rendered instead
 * (nudges the owner to complete the flow / discover the PA program).
 */
import React from "react";
import { Phone, Mail, MessageCircle, UserPlus } from "lucide-react";

export default function PaCard({ pa, compact = false, state }) {
  if (!pa || !pa.user) return <PaCardEmpty compact={compact} state={state} />;
  const u = pa.user;
  const fullName = [u.nome, u.cognome].filter(Boolean).join(" ") || u.email;
  const agency = [u.nome_agenzia, u.citta_agenzia].filter(Boolean).join(" · ");
  const initials = ((u.nome || "").charAt(0) + (u.cognome || "").charAt(0))
    .toUpperCase() || "PA";
  const phone = u.telefono;
  const email = u.email;
  const waHref = phone
    ? `https://wa.me/${phone.replace(/[^0-9]/g, "")}?text=${
      encodeURIComponent("Ciao, ti scrivo dal mio caso ValutaGenio.")
    }`
    : null;

  return (
    <div
      className={`rounded-2xl bg-white border border-gray-200 ${
        compact ? "p-4" : "p-6"
      }`}
      data-testid="owner-pa-card"
    >
      <p className="text-xs uppercase tracking-[0.14em] text-gray-400 mb-3">
        Il tuo Property Advisor
      </p>
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-[#B40101]/10 text-[#B40101]
                        flex items-center justify-center text-lg font-bold flex-shrink-0"
             data-testid="owner-pa-card-avatar">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[#0A0A0A] truncate" data-testid="owner-pa-card-name">
            {fullName}
          </p>
          {agency && (
            <p className="text-xs text-gray-500 truncate" data-testid="owner-pa-card-agency">
              {agency}
            </p>
          )}
        </div>
      </div>

      {!compact && (
        <div className="mt-5 pt-5 border-t border-gray-100 flex flex-wrap gap-2">
          {waHref && (
            <a href={waHref} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full
                          bg-[#25D366]/10 text-[#128C7E] text-sm font-medium
                          hover:bg-[#25D366]/15 transition"
               data-testid="owner-pa-card-whatsapp">
              <MessageCircle size={16} /> WhatsApp
            </a>
          )}
          {phone && (
            <a href={`tel:${phone}`}
               className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full
                          bg-gray-100 text-[#0A0A0A] text-sm font-medium hover:bg-gray-200 transition"
               data-testid="owner-pa-card-phone">
              <Phone size={16} /> Chiama
            </a>
          )}
          {email && (
            <a href={`mailto:${email}`}
               className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full
                          bg-gray-100 text-[#0A0A0A] text-sm font-medium hover:bg-gray-200 transition"
               data-testid="owner-pa-card-email">
              <Mail size={16} /> Email
            </a>
          )}
        </div>
      )}

      {!compact && pa.case_fit_explanation && (
        <p className="mt-4 text-sm text-gray-500 italic border-l-2 border-[#B40101]/30 pl-3"
           data-testid="owner-pa-card-fit">
          "{pa.case_fit_explanation}"
        </p>
      )}
    </div>
  );
}

const EMPTY_COPY = {
  requested: ["Richiesta inviata", "Stiamo individuando il Property Advisor che seguirà il tuo caso."],
  revoked: ["Consenso revocato", "Nessun Property Advisor ha accesso al tuo caso. Puoi presentare una nuova richiesta da AGISCI."],
};

function PaCardEmpty({ compact, state }) {
  const [title, text] = EMPTY_COPY[state] || ["Nessun Property Advisor assegnato",
    "Completa un'analisi e ti collegheremo a un consulente della tua zona."];
  return (
    <div className={`rounded-2xl bg-gray-50 border border-dashed border-gray-300 ${
        compact ? "p-4" : "p-6"
      } text-center`}
      data-testid="owner-pa-card-empty" data-state={state || "none"}>
      <UserPlus className="mx-auto text-gray-400 mb-2" size={compact ? 20 : 28} />
      <p className="text-sm font-medium text-[#0A0A0A]" data-testid="owner-pa-card-empty-title">{title}</p>
      {!compact && (
        <p className="text-xs text-gray-500 mt-1 max-w-xs mx-auto">{text}</p>
      )}
    </div>
  );
}
